import {
    NyuDataPoint,
    NyuDataPointWithParsedTime,
    NyuGroupProperties,
    NyuLineProperties,
    NyuSeriesProperties,
} from '../../../shared/types'
import { fallbackApiRoot } from './options'

export const getGroupsFromSeriesProperties = (
    seriesProperties: NyuSeriesProperties
): NyuGroupProperties[] => {
    const groups: NyuGroupProperties[] = seriesProperties.reduce(
        (acc: NyuGroupProperties[], cur: NyuLineProperties) => {
            const groupIndex = acc.findIndex(({ id }) => id === cur.group)
            if (groupIndex === -1) {
                acc.push({ id: cur.group, firstLine: cur, lines: [cur] })
            } else {
                acc[groupIndex].lines.push(cur)
            }
            return acc
        },
        []
    )
    return groups
}

export const getStartEndYearOptionsFromYears = (years: number[]) => {
    const maxMin = {
        start: Math.min(...years),
        end: Math.max(...years),
    }

    const startOptions = Array.from(
        { length: maxMin.end - maxMin.start },
        (_, i) => maxMin.start + i
    ).map(String)

    const endOptions = Array.from(
        { length: maxMin.end - maxMin.start },
        (_, i) => maxMin.start + i + 1
    ).map(String)

    return { startOptions, endOptions }
}

export function unwrap<T>(valueInArray: T[] | T): T {
    if (Array.isArray(valueInArray)) {
        return valueInArray[0]
    }
    return valueInArray
}

export function isDevelopmentDomain() {
    const { hostname, port } = window.location
    if (hostname === 'gct.rheindigital.org' && port !== '3000') {
        return true
    }
    if (hostname === 'localhost' && port === '1234') {
        return true
    }
    if (hostname === '127.0.0.1' && port === '1234') {
        return true
    }
    return false
}

export const unwrapValueInArray = (
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    wrappedValue?: string[] | {} | string
): string | null => {
    if (Array.isArray(wrappedValue) && wrappedValue.length > 0) {
        return wrappedValue.join(', ')
    }

    if (
        wrappedValue &&
        typeof wrappedValue === 'object' &&
        Object.keys(wrappedValue).length === 0
    ) {
        return null
    }

    return wrappedValue as string
}

function formatToThreeMeaningfulDigits(num: number): string {
    // Note: To avoid exponential notation, we need to convert the result of toPrecision() into an number and then back to a string
    const preciseNum = Number(num.toPrecision(3))
    return preciseNum.toString()
}

// Note: We are adding space after the value to fix values getting cut off from the x-axis
export const formatDataPointValue = (value: number, format: string) => {
    switch (format) {
        case '%':
            return formatToThreeMeaningfulDigits(value * 100) + '% '
        default:
            return `${formatToThreeMeaningfulDigits(value)} `
    }
}

export const limitStringAndAddEllipsis = (
    str: string,
    limit: number
): string => {
    if (str.length > limit) {
        return str.slice(0, limit) + '...'
    }
    return str
}

export const forecastPostfix = '_forecast'

export const isForecastDataKey = (dataKey: string) =>
    dataKey.includes(forecastPostfix)

// A `_highlight` series draws a stretch of its base line in a style of its own
// (e.g. dotted for values to be used with caution). Unlike a forecast it says
// nothing about the values: no asterisk, and no column of its own in the download.
export const highlightPostfix = '_highlight'

export const isHighlightDataKey = (dataKey: string) =>
    dataKey.includes(highlightPostfix)

export const toBaseDataKey = (dataKey: string) =>
    dataKey.replace(forecastPostfix, '').replace(highlightPostfix, '')

// A `group` is what the reader sees as one thing: one switch, one average, one
// name. A `_forecast` or `_highlight` series sharing its base series' group is
// therefore named after it; one with a group of its own carries the label its
// author gave it.
export const resolveSeriesKey = (
    dataKey: string,
    seriesProperties: NyuSeriesProperties
) => {
    if (!isForecastDataKey(dataKey) && !isHighlightDataKey(dataKey)) {
        return dataKey
    }

    const baseKey = toBaseDataKey(dataKey)
    const series = seriesProperties.find((item) => item.key === dataKey)
    const baseSeries = seriesProperties.find((item) => item.key === baseKey)

    if (!series) {
        return baseKey
    }
    if (!baseSeries) {
        return dataKey
    }

    return series.group === baseSeries.group ? baseKey : dataKey
}

export const selectizeLabelToPathSegment = (label: string = ''): string => {
    if (!label) {
        return ''
    }

    return `${label.replace('Select ', '')}: `
}

export const customOptions = [
    'Custom',
    'Custom...',
    'Customize',
    'Customize...',
]

export const isCustomOption = (option?: string): [boolean, boolean, string] => {
    if (!option) {
        return [false, false, '']
    }

    const isCustomLabel = customOptions.includes(option)

    const customMarker = '%custom%'
    const defaultMarker = '%default%'
    const containsCustomMarker = option.includes(customMarker)
    const isDefault = option.includes(defaultMarker)

    const isCustom = isCustomLabel || containsCustomMarker
    const optionText = option
        .replace(customMarker, '')
        .replace(defaultMarker, '')
        .trim()

    return [isCustom, isDefault, optionText]
}

function parseTimeAsYear(dataPoint: NyuDataPoint, time: number) {
    const hasTime = time > 0
    return {
        ...dataPoint,
        time,
        year: time,
        tooltipTimeValue: hasTime ? time.toString() : '',
        axisLabelTimeValue: time.toString().slice(-2),
        tooltipTimeLabel: hasTime ? 'Year' : '',
    }
}

export const parseTimeValue = (
    dataPoint: NyuDataPoint
): NyuDataPointWithParsedTime => {
    const { time } = dataPoint
    // The time-value can be:
    // "time": 2004, // number, as currently used
    // "time": "2001-12", // example for monthly data
    // "time": "2001Q4", // example for quarterly data
    // "time": "2001H1", // example for semiannual data
    if (typeof time === 'number') {
        return parseTimeAsYear(dataPoint, time)
    }
    if (typeof time === 'string') {
        // for semiannual, quarterly or monthly datapoints, the time is moved by a half-year, quarter
        // or month offset. in order for the year filters to work correctly, we need to store the
        // "source" year of the data point, since the adjusted time is not always the same as the
        // year in the data point.
        const year = parseInt(time.slice(0, 4))
        if (time.indexOf('Q') > -1) {
            const quarter = time.slice(5, 6)
            const quarterOffset = -0.5 + 1 / 8 // adjust so the middle of the year is between Q2 and Q3
            return {
                ...dataPoint,
                time: year + (parseInt(quarter, 10) - 1) / 4 + quarterOffset,
                year,
                tooltipTimeValue: `Q${quarter} ${year}`,
                axisLabelTimeValue: `Q${quarter}/${year.toString().slice(-2)}`,
                tooltipTimeLabel: 'Quarter',
            }
        }
        if (time.indexOf('H') > -1) {
            const half = time.slice(5, 6)
            const halfYearOffset = -0.5 + 1 / 4 // adjust so the middle of the year is between H1 and H2
            return {
                ...dataPoint,
                time: year + (parseInt(half, 10) - 1) / 2 + halfYearOffset,
                year,
                tooltipTimeValue: `H${half} ${year}`,
                axisLabelTimeValue: `H${half}/${year.toString().slice(-2)}`,
                tooltipTimeLabel: 'Half-year',
            }
        }
        if (time.indexOf('-') > -1) {
            const monthOffset = -0.5 + 1 / 24 // adjust so the middle of the year is between months 6 and 7
            const month = time.slice(5, 7)
            return {
                ...dataPoint,
                time: year + (parseInt(month, 10) - 1) / 12 + monthOffset,
                year,
                tooltipTimeValue: `${month} / ${year}`,
                axisLabelTimeValue: `${parseInt(month, 10)}/${year.toString().slice(-2)}`,
                tooltipTimeLabel: 'Month',
            }
        }
        // fallback for just a year as a string
        const timeInt = parseInt(time, 10)
        if (!isNaN(timeInt)) {
            return parseTimeAsYear(dataPoint, timeInt)
        }

        throw new Error(`Unsupported time format: ${time}`)
    }
    throw new Error(`Unsupported time type: ${typeof time}`)
}

export const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max)
}

export function getApiRoot(dataUrl: string) {
    /*
    API-Root-URLs are used to append the short chunk-links (the filename of the chunk)
    Example API-Host: 
    - https://gc-tracker-files.nyc3.cdn.digitaloceanspaces.com
    - https://www.dhl.com/fragments/gct-v2/nyu
    Example Data-URLs: 
    - https://gc-tracker-files.nyc3.cdn.digitaloceanspaces.com/processed-data/roots/<collectionName>.json
    - https://www.dhl.com/fragments/gct-v2/nyu/processed-data/roots/<collectionName>.json
    */
    const processedDataSegment = '/processed-data'
    const apiRoot = dataUrl.split(processedDataSegment)[0]
    return apiRoot || fallbackApiRoot
}

function getCollectionName(dataUrl: string) {
    const segments = dataUrl.split('/')
    const collectionName = segments.pop()?.split('.')[0] || ''
    return collectionName
}

export function getChunkUrl(dataUrl: string, chunkFilename: string) {
    const chunkUrl = `${getApiRoot(dataUrl)}/processed-data/chunks/${getCollectionName(dataUrl)}/${chunkFilename}`
    return chunkUrl
}
